<template>
  <div ref="goodsCardRootRef">
    <div
      v-if="showPlatformTabs"
      class="goods-platform-tabs"
    >
      <button
        v-for="(item, index) in platformTabs"
        :key="item.value"
        type="button"
        :class="[
          'goods-platform-tab',
          { 'goods-platform-tab--active': item.value === activePlatform },
        ]"
        @click="handlePlatformClick(item, index)"
      >
        {{ getLocaleText(item.title || item.label || item.value) }}
      </button>
    </div>
    <div v-if="config.selectCategory" class="hot-sale-category-wrap">
      <div class="hot-sale-category-item">
        <div class="hot-sale-category-label">{{$t('placeholder.pleaseSelected{text}',{text:$t('texts.mainCategory')})}}</div>
        <el-select
          size="large"
          v-model="searchData.hotSaleTopCategory"
          @change="onHotSaleTopCategoryChange"
        >
          <el-option
            v-for="item in hotSaleTopOptions"
            :key="item.chinese_name"
            :label="item.chinese_name"
            :value="item.chinese_name"
          ></el-option>
        </el-select>
      </div>
      <div class="hot-sale-category-item">
        <div class="hot-sale-category-label">{{$t('placeholder.pleaseSelected{text}',{text:$t('texts.subCategory')})}}</div>
        <el-select
          size="large"
          v-model="searchData.hotSaleChildCategory"
          @change="onHotSaleChildCategoryChange"
        >
          <el-option
            v-for="item in hotSaleChildOptions"
            :key="item.chinese_name"
            :label="item.chinese_name"
            :value="item.chinese_name"
          ></el-option>
        </el-select>
      </div>
    </div>

    <div v-loading="loading" class="hot-sale-goods-box">
      <!-- Image List View -->
      <div v-if="config.showStyle === 'imageList'" class="hot-sale-goods-table-header">
        <div class="hot-sale-goods-table-header-item" style="flex:1;">{{$t('texts.goodsInfo')}}</div>
        <div class="hot-sale-goods-table-header-item" style="width: 169px;">{{$t('texts.price')}}</div>
        <div class="hot-sale-goods-table-header-item" style="width: 92px;">{{$t('texts.salesAmount')}}</div>
      </div>
      <component
        v-if="config.showStyle === 'imageList'"
        :is="config.isScroll ? ElScrollbar : 'div'"
        :style="{
            height:
              typeof config.arcHeight === 'number'
                ? config.arcHeight + 'px'
                : 'auto',
          }"
      >
        <div class="goods-list-wrap">
          <ListItem
            v-for="(item, i) in goodsList"
            :key="item.id"
            :item="item"
            :rank="i + 1"
            :source="config.recommendSource || 'system'"
            :tracking-config="config.trackingConfig"
            :tracking-element="config"
            :element-index="[...parentKeys, idx, i]"
          />
        </div>
      </component>

      <component
      v-if="config.showStyle === 'colList'"
      :is="config.isScroll ? ElScrollbar : 'div'"
      :style="{
          height:
            typeof config.arcHeight === 'number'
              ? config.arcHeight + 'px'
              : 'auto',
        }"
    >
      <div class="goods-col-list-wrap">
        <ListColItem
          v-for="(item, i) in goodsList"
          :key="item.id"
          :item="item"
          :rank="i + 1"
          :source="config.recommendSource || 'system'"
          :tracking-config="config.trackingConfig"
          :tracking-element="config"
          :element-index="[...parentKeys, idx, i]"
        />
      </div>
    </component>

      <!-- Pagination -->
      <div class="footer-wrap">
        <div class="footer-info-wrap" ><span class="c-#999 text-size-12px">{{getLocaleText(config.footerInfoHtml)}}</span></div>
        <div  class="current-page-wrap" v-if="config.isPagination && !config.pageLayout && !config.enableLoadMore">{{$t('placeholder.no{text}Page',{text:pageNo+1})}}</div>
        <el-pagination
          v-if="config.isPagination && !config.enableLoadMore"
          :currentPage="pageNo + 1"
          :page-size="config.isShowLength"
          :layout="config.pageLayout||'prev,next'"
          :total="total"
          @current-change="handleCurrentChange"
        />
      </div>
      <div v-if="config.enableLoadMore" class="load-more-wrap">
        <el-button
          type="primary"
          plain
          :loading="loadingMore"
          class="more-btn"
          :disabled="!hasMore"
          @click="handleLoadMore"
        >
          {{ loadingMore ? $t("tbDetail.loadingMore") : hasMore ? $t("tbDetail.loadMore") : $t("tbDetail.noMoreData") }}
        </el-button>
      </div>
    </div>
  </div>
</template>
  
  <script setup>
import { ref, computed, onMounted, reactive, watch, nextTick, inject } from "vue";
import { useRoute } from "vue-router";
import { useIntersectionObserver } from "@vueuse/core";
import { get1688GoodsList, tbGlobalGoodsItemList } from "@/api";
import { find, get, set } from "lodash-es";
import { hotSaleTreeListFn } from "@/components/render/mall/Nav/categoryConfig.js";
import {localeTo1688CountryMap} from "@/utils/globalData.js";

import useLocate from "@/components/render/uses/useLocate.js";
import { ElScrollbar } from "element-plus";
import { useMainStore } from "@/composables/store";
import ListItem from "@/components/render/mall/goods/card/ListItem.vue";
import ListColItem from "@/components/render/mall/goods/card/ListColItem.vue";
import { $t } from "~/utils/index.js";
import { trackLowCodeEvent } from "@/utils/lowCodeTracking.js";
let { langJson, nowLocale, getLocaleText } = useLocate();
const store = useMainStore();
 
const { locale } = useI18n()
// 接收props
const props = defineProps({
  allArcListMap: {
    type: Object,
    default: () => ({})
  },
  currentElementIndex: {
    type: Array,
    default: () => []
  },
  idx: {
    type: Number,
    default: -1
  },
  parentKeys: {
    type: Array,
    default: () => []
  },
  disabled: {
    type: Boolean,
    default: true
  },
  config: {
    type: Object,
    default: () => ({})
  }
});

function normalizePlatformValue(value) {
  const normalizedValue = String(value || "").trim();
  return normalizedValue === "tb" ? "taobao" : normalizedValue;
}

function resolvePlatformTab(item, index) {
  const value = normalizePlatformValue(item?.value || item?.tabVal);
  return {
    ...item,
    value: value || `platform-${index}`,
  };
}

const platformTabs = computed(() =>
  (Array.isArray(props.config.categoryConfigs)
    ? props.config.categoryConfigs
    : []
  ).map(resolvePlatformTab),
);
const showPlatformTabs = computed(
  () => !!props.config.isHaveCategory && platformTabs.value.length > 0,
);
const activePlatform = ref(
  showPlatformTabs.value
    ? platformTabs.value[0].value
    : normalizePlatformValue(props.config.platformType),
);
const effectivePlatformType = computed(() =>
  showPlatformTabs.value
    ? activePlatform.value
    : normalizePlatformValue(props.config.platformType),
);
const goodsCardRootRef = ref(null);
const initialPlatformReported = ref(false);
const getConfigJson = inject("getConfigJson", () => ({}));

// 响应式数据
const loading = ref(false);
const loadingMore = ref(false);
const pageNo = ref(0);
const total = ref(0);
const list = ref([]);

const searchData = reactive(initCategoryData());

const searchFormConfigs = [];

const isPc = computed(() => {
  return !["v/m", "siteweb", "web"].some(path =>
    useRoute().path.includes(path)
  );
});

const hotSaleTopOptions = computed(() => {
  return hotSaleTreeListFn();
});

const hotSaleChildOptions = computed(() => {
  let res = find(
    hotSaleTreeListFn(),
    o => o.chinese_name == searchData.hotSaleTopCategory
  );
  return res && res.childs;
});

// 计算属性：处理文章列表
const goodsList = computed(() => {
  return list.value;
});

const hasMore = computed(() => {
  if (!props.config.enableLoadMore) return false;
  return list.value.length < Number(total.value || 0);
});

watch(()=>locale.value,()=>{
  nextTick(()=>{
    let setSearchData= initCategoryData();
    Object.keys(setSearchData).map(k=>{
      searchData[k]=setSearchData[k];
    });
  })
});

const responseDataFilterMap = {
  //过滤器
  "1688": function(responseData) {
    if (!get(responseData, "data.dataList") && get(responseData, "data.data")) {
      set(responseData, "data.dataList", get(responseData, "data.data"));
    }
    if (
      !get(responseData, "data.totalRecord") &&
      get(responseData, "data.totalRecords")
    ) {
      set(
        responseData,
        "data.totalRecord",
        get(responseData, "data.totalRecords")
      );
    }
    if (get(responseData, "data.dataList")) {
      responseData.data.dataList = responseData.data.dataList.map(o => {
        return {
          ...o,
          id: o.offerId,
          pic: o.imageUrl,
          iconType: "1688",
          platform: "1688",
          link: `/product?detailId=${o.offerId}`,
          title:localeTo1688CountryMap[locale.value]?o.subjectTrans: o.subject,
          finalPrice: o.priceInfo?.price,
          transferPrice: o.priceInfo?.price,
          salesNum: o.monthSold
        };
      });
    }

    return responseData;
  },
  "taobao": function(responseData) {
    const directData = get(responseData, "data");
    const rawList =
      get(responseData, "data.dataList")
      ?? (Array.isArray(directData) ? directData : undefined)
      ?? get(responseData, "data.data.dataList")
      ?? get(responseData, "data.data")
      ?? get(responseData, "data.data.data")
      ?? get(responseData, "data.records")
      ?? get(responseData, "data.list")
      ?? [];
    const rawTotal =
      get(responseData, "data.totalRecord")
      ?? get(responseData, "data.data.totalRecord")
      ?? get(responseData, "data.data.data.totalRecord")
      ?? get(responseData, "data.totalRecords")
      ?? get(responseData, "data.data.totalRecords")
      ?? get(responseData, "data.total")
      ?? (Array.isArray(rawList) ? rawList.length : 0);

    set(
      responseData,
      "data.dataList",
      Array.isArray(rawList)
        ? rawList.map(o => {
            const itemId = String(
              o.itemId || o.id || o.item_id || o.itemID || ""
            ).trim();
            const imageUrl = String(
              o.mainImageUrl || o.image || o.pictUrl || o.pic || ""
            ).trim();
            const rawPrice =
              o.finalPrice ??
              o.price ??
              o.discountPrice ??
              o.zkFinalPrice ??
              o.reservePrice;
            const rawOriginalPrice =
              o.reservePrice ??
              o.zkFinalPrice ??
              o.price ??
              o.originalPrice ??
              o.originPrice ??
              o.linePrice;
            const finalPrice = normalizeTaobaoPrice(rawPrice);
            const originalPrice = normalizeTaobaoPrice(rawOriginalPrice);

            return {
              ...o,
              id: itemId,
              itemId,
              pic: imageUrl,
              imageUrl,
              iconType: "TB",
              link: itemId ? `/tb/goods/${encodeURIComponent(itemId)}` : "#",
              title: o.multiLanguageInfo?.title || o.title,
              finalPrice,
              transferPrice: finalPrice,
              originalPrice,
              salesNum:
                o.saleQuantity ??
                o.salesNum ??
                o.sellCount ??
                o.volume ??
                o.soldCount ??
                o.sales,
              platform: "taobao"
            };
          })
        : []
    );
    set(responseData, "data.totalRecord", Number(rawTotal || 0) || 0);
    return responseData;
  }
};

function normalizeTaobaoPrice(rawPrice) {
  const price = Number(rawPrice);
  if (!Number.isFinite(price)) {
    return rawPrice;
  }

  // return Number.isInteger(price) ? price / 100 : price;
  return price;
}

// 获取文章列表数据
const fetchFunc = async (fetchData = {}, options = {}) => {
  try {
    const appendMode = !!options.append;
    if (appendMode) {
      loadingMore.value = true;
    } else {
      loading.value = true;
    }
    let responseData = null;
    let mergeData={}
    try{
      mergeData=JSON.parse(props.config.mergeFetchData)
    }catch(e){
      console.log(e)
    }
    if (effectivePlatformType.value == "1688") {
      // 1688
      
      responseData = await get1688GoodsList({
        param: {
          pageSize: props.config.isShowLength,
          beginPage: pageNo.value+1,
          country: localeTo1688CountryMap[locale.value]||'en',
          keyword: searchData.hotSaleChildCategory,
          // outMemberId:
          ...fetchData,
          ...mergeData
        }
      });
      console.log(responseData,'====')
    } else if (effectivePlatformType.value == "taobao") {
      responseData = await tbGlobalGoodsItemList({
        param: {
          pageNo: pageNo.value + 1,
          pageSize: props.config.isShowLength,
          keyword: searchData.hotSaleChildCategory || undefined,
          ...fetchData,
          ...mergeData
        }
      });
    }
    if (responseDataFilterMap[effectivePlatformType.value]) {
      responseData = responseDataFilterMap[effectivePlatformType.value](
        responseData
      );
    }

    const nextList = get(responseData, "data.dataList", []).map(o => ({ ...o }));
    list.value = appendMode ? [...list.value, ...nextList] : nextList;
    total.value = get(responseData, "data.totalRecord", 0) || 0;
  } catch (e) {
    console.log(e);
  } finally {
    loading.value = false;
    loadingMore.value = false;
  }
};

function initCategoryData() {
  let initData = {
    hotSaleTopCategory: hotSaleTreeListFn()[0].chinese_name,
    hotSaleChildCategory: hotSaleTreeListFn()[0].childs[0].chinese_name
  };
  let categorys=[]
  if(props.config.defaultCategorys){
    let categorysIdx=props.config.defaultCategorys.split(",");
    categorysIdx=categorysIdx.map(o=>{
      return isNaN(Number(o))?0:Number(o);
    })
    if(typeof categorysIdx[0]!='undefined'){
      categorys[0]=get(hotSaleTreeListFn(),`[${categorysIdx[0]}].chinese_name`);
    }
    if(typeof categorysIdx[1]!='undefined'){
      let childVal=get(hotSaleTreeListFn(),`[${categorysIdx[0]}].childs[${categorysIdx[1]}].chinese_name`);
      if(childVal){
        categorys[1]=childVal;
      }
    }
  }
  
  
  initData.hotSaleTopCategory=categorys[0]||hotSaleTreeListFn()[0].chinese_name;
  initData.hotSaleChildCategory=categorys[1]||hotSaleTreeListFn()[0].childs[0].chinese_name
  return initData;
}
// 分页变化
const handleCurrentChange = data => {
  pageNo.value = data - 1;
  fetchFunc();
};

const handleLoadMore = () => {
  if (loadingMore.value || !hasMore.value) return;
  pageNo.value += 1;
  fetchFunc({}, { append: true });
};

function onHotSaleTopCategoryChange(data) {
  nextTick(() => {
    searchData.hotSaleChildCategory = hotSaleChildOptions.value[0].chinese_name;
    onHotSaleChildCategoryChange();
  });
}

function onHotSaleChildCategoryChange() {
  pageNo.value = 0;
  fetchFunc();
}

function reportPlatform(item, index, interaction) {
  if (!item) return false;
  const page = typeof getConfigJson === "function" ? getConfigJson() : {};
  const platformName = getLocaleText(item.title || item.label || item.value);
  return trackLowCodeEvent(props.config.platformTrackingConfig, {
    trigger: "click",
    element: item,
    elementType: "mall_goods_card_platform",
    elementIndex: [...props.parentKeys, props.idx, "platform", index],
    page,
    binding: {
      ...(page?.bindData || {}),
      platformData: item,
      platformIndex: index,
      platformName,
    },
    runtime: {
      platformInteraction: interaction,
      platformType: item.value,
      platformTitle: platformName,
      platformName,
      platformIndex: index,
    },
    eventParams: { platformName },
  });
}

function reportInitialPlatform() {
  if (!showPlatformTabs.value || initialPlatformReported.value) return;
  const platformIndex = platformTabs.value.findIndex(
    (item) => item.value === activePlatform.value,
  );
  if (platformIndex < 0) return;
  if (reportPlatform(platformTabs.value[platformIndex], platformIndex, "visible")) {
    initialPlatformReported.value = true;
  }
}

function handlePlatformClick(item, index) {
  if (!item?.value || item.value === activePlatform.value) return;
  activePlatform.value = item.value;
  if (reportPlatform(item, index, "click")) {
    initialPlatformReported.value = true;
  }
  pageNo.value = 0;
  nextTick(() => fetchFunc());
}

useIntersectionObserver(goodsCardRootRef, ([entry]) => {
  if (entry?.isIntersecting) {
    reportInitialPlatform();
  }
});

watch(
  () => [
    props.config.isHaveCategory,
    props.config.platformType,
    platformTabs.value.map((item) => item.value).join("|"),
  ],
  () => {
    const nextPlatform = showPlatformTabs.value
      ? platformTabs.value[0]?.value
      : normalizePlatformValue(props.config.platformType);
    if (!nextPlatform || nextPlatform === activePlatform.value) return;
    activePlatform.value = nextPlatform;
    initialPlatformReported.value = false;
    pageNo.value = 0;
    nextTick(() => fetchFunc());
  },
);

// 搜索表单数据变化
const onFormDataChange = v => {
  searchData.value = v;
  fetchFunc(v);
};

 




// 组件挂载时获取数据
onMounted(() => {
  fetchFunc();
});
</script>
  

<style scoped lang="less">
.hot-sale-category-wrap {
  display: flex;
  align-items: center;
  margin-bottom: 24px;

  .hot-sale-category-item {
    flex: 1;
    margin-right: 10px;
    .hot-sale-category-label {
      color: #999;
      margin-bottom: 10px;
    }
  }
  .hot-sale-category-item:last-child {
    margin-right: 0px;
  }
}
.goods-platform-tabs {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 16px;
  border-bottom: 1px solid #ebeef5;
}
.goods-platform-tab {
  appearance: none;
  border: 0;
  border-bottom: 2px solid transparent;
  padding: 10px 2px;
  background: transparent;
  color: #606266;
  cursor: pointer;
  font: inherit;
}
.goods-platform-tab--active {
  border-bottom-color: var(--el-color-primary);
  color: var(--el-color-primary);
}
.hot-sale-goods-table-header {
  display: flex;
  .hot-sale-goods-table-header-item {
    color: #999;
    font-size: 14px;
    min-height: 42px;
    display: flex;
    align-items: center;
  }
}
 
.footer-wrap {
  display: flex;
  align-items: center;
  .footer-info-wrap {
    flex: 1;
    min-width: 100px;
  }
  .current-page-wrap {
    font-size: 14px;
    color: #999;
    margin-right: 20px;
  }
}
.load-more-wrap {
  display: flex;
  justify-content: center;
  margin-top: 36px;
}
.more-btn{
  width: 200px;
  height: 44px;
  font-size: 20px;
  background-color: transparent;
  border-radius: 2px;
  &:hover{
    background-color:var(--el-color-primary);
  }
}
.goods-col-list-wrap{
  width: calc( 100% + 20px );
  display: flex;
  flex-wrap: wrap;
}
 
</style>
